import { PageContainer } from '@ant-design/pro-layout'
import { makeStyles } from '@material-ui/styles'
import DurationFilterButton from '@root/src/components/DurationFilterButton'
import FilterDialog from '@root/src/components/FilterDialog'
import {
  Button,
  Col,
  Modal,
  Pagination,
  Row,
  Spin,
  Table,
  Typography as Text,
  Tabs,
  Collapse,
} from 'antd'
import { saveAs } from 'file-saver'
import _ from 'lodash'
import moment from 'moment'
import { useEffect, useState, useCallback, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
// eslint-disable-next-line import/no-webpack-loader-syntax
import Worker from 'worker-loader!./worker/statements.worker.js'

import ReportCard from '@/components/ReportCard'
import dimorderApi from '@/libs/api/dimorderApi'
import { useSelector } from '@/redux'
import colors from '@/style/colors'

import { getDimorderStatementErrorKey } from './errors'

const { Panel } = Collapse
const STATEMENT_PAGE_SIZE = 10
const STATEMENT_PAGE_SIZE_OPTIONS = ['10', '50', '100']

const tabs = [
  { id: 'storeDelivery', i18nKey: 'app.constant.deliveryType.dimorderTakeaway', text: '外賣' },
  { id: 'table', i18nKey: 'app.constant.deliveryType.table', text: '堂食' },
]

const parseStatementRows = (statements) => {
  const dateFormatter = (date) => moment(date).format('YYYY-MM-DD')
  return _.map(statements, (statement) => {
    const { jobId, downloadLink, startedAt, endedAt } = statement
    const formattedDate = `[ ${dateFormatter(startedAt)} ] - [ ${dateFormatter(endedAt)} ]`
    return {
      key: jobId,
      date: formattedDate,
      downloadUrl: downloadLink,
    }
  })
}

export default function DimorderStatement(props) {
  const classes = useStyles(props)
  const { t } = useTranslation()
  const activeMerchant = useSelector((state) => state.merchant.activeMerchant)
  const [selectedRows, setSelectedRows] = useState([])
  const [selectedTab, setSelectedTab] = useState(tabs[0].id)
  const [loading, setLoading] = useState(false)
  const [params, setParams] = useState(null)
  const [activeKey, setActiveKey] = useState('0')
  const [isSearchHide, setIsSearchHide] = useState(true)
  const [isBatchDownloading, setIsBatchDownloading] = useState(false)
  const [isShowingTimeLabel, setIsShowingTimeLabel] = useState(false)
  const [totalStatements, setTotalStatements] = useState(0)
  const [statementPages, setStatementPages] = useState({})
  const [pageCursors, setPageCursors] = useState({})
  const [currentPage, setCurrentPage] = useState(1)
  const [statementPageSize, setStatementPageSize] = useState(STATEMENT_PAGE_SIZE)
  const [isLoadingPage, setIsLoadingPage] = useState(false)
  const statementRequestId = useRef(0)
  const displayData = statementPages[currentPage] || []

  const resetState = () => {
    const start = moment(0).toISOString()
    const end = moment().toISOString()

    setParams({ start, end })
    setIsSearchHide(true)
    setActiveKey('0')
    setIsShowingTimeLabel(false)
  }

  const setStateParams = (props) => {
    setParams(props)
    setIsShowingTimeLabel(true)
  }

  useEffect(() => {
    if (!(params?.start && params?.end)) {
      resetState()
    }
  }, [params])

  useEffect(() => {
    // 在報表頁面切換餐廳時，清空所有資料
    resetState()
  }, [activeMerchant?.id])

  useEffect(() => {
    if (!params || !activeMerchant?.id) return
    const requestId = ++statementRequestId.current
    setLoading(true)
    setIsLoadingPage(false)
    setStatementPages({})
    setPageCursors({ 1: null })
    setCurrentPage(1)
    setSelectedRows([])
    setTotalStatements(0)

    const fetchFirstPage = async () => {
      try {
        const statementResponse = await dimorderApi.instance(activeMerchant.id)?.dimorderStatement({
          deliveryType: selectedTab,
          ...params,
          limit: statementPageSize,
        })
        if (requestId !== statementRequestId.current) return

        const statements = statementResponse?.data || []
        setTotalStatements(statementResponse?.total ?? statements.length)
        setStatementPages({ 1: parseStatementRows(statements) })
        setPageCursors(
          statementResponse?.next ? { 1: null, 2: statementResponse.next } : { 1: null },
        )

        setActiveKey('0')
        setIsSearchHide(true)
      } catch (error) {
        if (requestId !== statementRequestId.current) return
        console.error(error)
        Modal.error({
          title: t('app.common.error'),
          content: t(`page.dimorderStatement.${getDimorderStatementErrorKey(error)}`),
        })
        setActiveKey('1')
        setIsSearchHide(false)
      } finally {
        if (requestId === statementRequestId.current) setLoading(false)
      }
    }

    fetchFirstPage()

    return () => {
      if (requestId === statementRequestId.current) statementRequestId.current += 1
    }
  }, [activeMerchant?.id, selectedTab, params, statementPageSize])

  useEffect(() => {
    // 切換 tab 或時間範圍時清除上一個查詢的選取項目
    setSelectedRows([])
  }, [selectedTab, params, activeMerchant?.id])

  const handlePageChange = useCallback(
    async (page) => {
      if (page === currentPage || isLoadingPage) return
      if (statementPages[page]) {
        setSelectedRows([])
        setCurrentPage(page)
        return
      }
      if (!params || !activeMerchant?.id) return

      const requestId = statementRequestId.current
      setIsLoadingPage(true)
      const pages = { ...statementPages }
      const cursors = { ...pageCursors }
      try {
        for (let pageNumber = 2; pageNumber <= page; pageNumber += 1) {
          if (pages[pageNumber]) continue
          const cursor = cursors[pageNumber]
          if (!cursor) break

          const statementResponse = await dimorderApi
            .instance(activeMerchant.id)
            ?.dimorderStatement({
              deliveryType: selectedTab,
              ...params,
              after: cursor,
              limit: statementPageSize,
            })
          if (requestId !== statementRequestId.current) return

          const statements = statementResponse?.data || []
          pages[pageNumber] = parseStatementRows(statements)
          if (statementResponse?.next) {
            cursors[pageNumber + 1] = statementResponse.next
          } else {
            delete cursors[pageNumber + 1]
          }
          if (statementResponse?.total != null) setTotalStatements(statementResponse.total)
        }

        if (requestId === statementRequestId.current) {
          setStatementPages(pages)
          setPageCursors(cursors)
          const lastLoadedPage = Math.max(1, ...Object.keys(pages).map(Number))
          setSelectedRows([])
          setCurrentPage(pages[page] ? page : lastLoadedPage)
        }
      } catch (error) {
        if (requestId !== statementRequestId.current) return
        console.error(error)
        Modal.error({
          title: t('app.common.error'),
          content: t(`page.dimorderStatement.${getDimorderStatementErrorKey(error)}`),
        })
      } finally {
        if (requestId === statementRequestId.current) setIsLoadingPage(false)
      }
    },
    [
      activeMerchant?.id,
      currentPage,
      isLoadingPage,
      pageCursors,
      params,
      selectedTab,
      statementPageSize,
      statementPages,
      t,
    ],
  )

  const LoadingSpin = useCallback(() => {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          margin: 60,
        }}
      >
        <Spin size='large' />
      </div>
    )
  }, [])

  const BatchDownloadButton = useCallback(
    (props) => {
      const { selectedRows, isBatchDownloading } = props
      const isDisabled = selectedRows.length === 0 || isBatchDownloading
      return (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            disabled={isDisabled}
            className={classes.btn}
            onClick={() => {
              setIsBatchDownloading(true)
              const worker = new Worker()
              worker.postMessage({ selectedRows })
              worker.addEventListener('message', (message) => {
                const { downloadable } = message.data
                worker.terminate()
                saveAs(
                  downloadable,
                  `${activeMerchant?.name} - ${t('page.dimorderStatement.statement')}.zip`,
                )
                setIsBatchDownloading(false)
              })
            }}
          >
            {isBatchDownloading ? (
              <Spin size='small' />
            ) : (
              t('page.dimorderStatement.batch_download')
            )}
          </Button>
        </div>
      )
    },
    [activeMerchant, classes.btn, t],
  )

  const DownloadButton = useCallback(
    (props) => {
      const { downloadUrl } = props
      return (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            className={classes.btn}
            onClick={() => {
              window.location.href = downloadUrl
            }}
          >
            {t('page.dimorderStatement.download')}
          </Button>
        </div>
      )
    },
    [classes.btn, t],
  )

  const renderDownloadButton = useCallback(
    (downloadUrl, record) => {
      if (record.downloadType === 'all') {
        return (
          <BatchDownloadButton
            selectedRows={selectedRows}
            isBatchDownloading={isBatchDownloading}
          />
        )
      }
      return <DownloadButton downloadUrl={downloadUrl} />
    },
    [BatchDownloadButton, DownloadButton, selectedRows, isBatchDownloading],
  )

  const tableData = useMemo(
    () =>
      displayData.length > 0
        ? [{ key: 'download-all-statements', downloadType: 'all' }, ...displayData]
        : displayData,
    [displayData],
  )

  const rowSelection = useMemo(
    () => ({
      type: 'checkbox',
      selectedRowKeys: selectedRows.map((row) => row.key),
      getCheckboxProps: (record) => ({
        style: { opacity: record.downloadType === 'all' ? 0 : 1 },
        disabled: record.downloadType === 'all',
      }),
      onChange: (_newSelectedRowKeys, newSelectedRows) => setSelectedRows(newSelectedRows),
    }),
    [displayData, selectedRows],
  )

  const columns = useMemo(
    () => [
      {
        title: t('page.dimorderStatement.date'),
        dataIndex: 'date',
      },
      {
        title: t('page.dimorderStatement.download'),
        dataIndex: 'downloadUrl',
        render: renderDownloadButton,
      },
    ],
    [renderDownloadButton],
  )

  return (
    <PageContainer ghost header={{ title: t('page.dimorderStatement.title') }}>
      <Collapse
        ghost
        expandIcon={() => <></>}
        activeKey={activeKey}
        onChange={(key) => {
          if (typeof key === 'object') {
            setActiveKey(key?.[key.length - 1] ?? '0')
          } else {
            setActiveKey(key)
          }
        }}
      >
        <Panel
          key='1'
          style={{ fontSize: 22 }}
          extra={
            <DurationFilterButton
              showLabel={isShowingTimeLabel}
              start={params?.start}
              end={params?.end}
              activeKey={activeKey}
            />
          }
        >
          <FilterDialog filterTime setParams={setStateParams} />
        </Panel>
      </Collapse>
      <br />
      {isSearchHide && (
        <>
          <Row style={{ display: 'flex', justifyContent: 'center' }}>
            <Col xs={24} xl={14}>
              <ReportCard>
                <Text className={classes.title}>
                  {`${activeMerchant?.name} - ${t('page.dimorderStatement.statement')}`}
                </Text>
                <br />
                <Tabs
                  defaultActiveKey='storeDelivery'
                  centered
                  className={classes.tab}
                  onChange={(e) => {
                    setSelectedTab(e)
                    setIsSearchHide(true)
                  }}
                  destroyInactiveTabPane
                >
                  {tabs.map((tab) => (
                    <Tabs.TabPane tab={t(tab.i18nKey)} key={tab.id}>
                      {loading ? (
                        <LoadingSpin />
                      ) : (
                        <>
                          <Table
                            rowSelection={rowSelection}
                            columns={columns}
                            dataSource={tableData}
                            rowKey='key'
                            pagination={false}
                          />
                          {totalStatements > 0 && (
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                gap: 12,
                                marginTop: 16,
                                flexWrap: 'wrap',
                              }}
                            >
                              <Text type='secondary'>
                                {t('page.dimorderStatement.showing', {
                                  from: (currentPage - 1) * statementPageSize + 1,
                                  to: Math.min(currentPage * statementPageSize, totalStatements),
                                  total: totalStatements,
                                })}
                              </Text>
                              <Pagination
                                current={currentPage}
                                pageSize={statementPageSize}
                                total={totalStatements}
                                showSizeChanger
                                pageSizeOptions={STATEMENT_PAGE_SIZE_OPTIONS}
                                locale={{
                                  items_per_page: t('page.dimorderStatement.perPage'),
                                  prev_page: t('page.dimorderStatement.previousPage'),
                                  next_page: t('page.dimorderStatement.nextPage'),
                                }}
                                disabled={isLoadingPage}
                                onChange={(page, size) => {
                                  if (size !== statementPageSize) {
                                    setStatementPageSize(size)
                                    return
                                  }
                                  handlePageChange(page)
                                }}
                              />
                            </div>
                          )}
                        </>
                      )}
                    </Tabs.TabPane>
                  ))}
                </Tabs>
              </ReportCard>
            </Col>
          </Row>
        </>
      )}
    </PageContainer>
  )
}

const useStyles = makeStyles({
  btn: {
    borderColor: colors.button,
    borderRadius: 5,
    color: colors.button,
  },
  title: {
    fontSize: 18,
    textAlign: 'center',
  },
  tab: {
    '& .ant-tabs-nav-list': {
      width: '100%',
    },
    '& .ant-tabs-tab': {
      justifyContent: 'center',
      display: 'flex',
      width: `${(1 / tabs.length) * 100 - 1}%`,
    },
    '& .ant-tabs-nav-operations': {
      display: 'none !important',
    },
  },
})
